import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import './OrganizationModals.css';

const EditOrganizationModal = ({ show, organization, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        target: '',
        managerIds: [],
        active: true
    });
    const [managers, setManagers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Add month/year selection for target
    const currentDate = new Date();
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [targetHistory, setTargetHistory] = useState([]);

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    useEffect(() => {
        if (show && organization) {
            setFormData({
                name: organization.name || '',
                target: '',
                // managerIds may arrive populated (array of user objects) or as raw ids
                managerIds: (organization.managerIds || []).map(m => (m && typeof m === 'object' ? m._id : m)),
                active: organization.active !== false
            });
            fetchManagers();
            fetchCurrentTarget();
            fetchTargetHistory();
        }
    }, [show, organization, selectedMonth, selectedYear]);

    const fetchManagers = async () => {
        try {
            const response = await axiosInstance.get('/api/users', {
                params: { role: 'sales_manager', active: true }
            });
            if (response.data.success) {
                setManagers(response.data.users || []);
            }
        } catch (err) {
            console.error('Error fetching managers:', err);
        }
    };

    const fetchCurrentTarget = async () => {
        try {
            const response = await axiosInstance.get('/api/sales-target/organization-targets', {
                params: {
                    organizationId: organization._id,
                    month: selectedMonth,
                    year: selectedYear
                }
            });
            const target = response.data.success ? response.data.data.target : 0;
            setFormData(prev => ({ ...prev, target: target || '' }));
        } catch (err) {
            // Fallback to organization.target if monthly target not found
            setFormData(prev => ({ ...prev, target: organization.target || '' }));
        }
    };

    const fetchTargetHistory = async () => {
        try {
            const response = await axiosInstance.get('/api/sales-target/organization-targets/history', {
                params: {
                    organizationId: organization._id,
                    limit: 6
                }
            });
            if (response.data.success) {
                setTargetHistory(response.data.data || []);
            }
        } catch (err) {
            console.error('Error fetching target history:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Update organization basic info (name, manager, active status)
            const orgPayload = {
                name: formData.name,
                managerIds: formData.managerIds,
                active: formData.active
            };

            await axiosInstance.put(
                `/api/sales-target/organizations/${organization._id}`,
                orgPayload
            );

            // Set the monthly target if provided
            if (formData.target && parseFloat(formData.target) > 0) {
                await axiosInstance.post('/api/sales-target/organization-targets', {
                    organizationId: organization._id,
                    month: selectedMonth,
                    year: selectedYear,
                    target: parseFloat(formData.target)
                });
            }

            onSuccess();
            handleClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update organization');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({ name: '', target: '', managerIds: [], active: true });
        setTargetHistory([]);
        setError(null);
        onClose();
    };

    const toggleManager = (id) => {
        setFormData(prev => ({
            ...prev,
            managerIds: prev.managerIds.includes(id)
                ? prev.managerIds.filter(m => m !== id)
                : [...prev.managerIds, id]
        }));
    };

    if (!show || !organization) return null;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                <h2>Edit Organization</h2>
                
                <div className="org-info">
                    <span className={`type-badge ${organization.type}`}>
                        {organization.type === 'root' && '🏢 Root'}
                        {organization.type === 'company' && '🏭 Company'}
                        {organization.type === 'branch' && '🏪 Branch'}
                    </span>
                </div>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Name *</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Enter organization name"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Managers</label>
                        <div className="manager-checkbox-list">
                            {managers.length === 0 && (
                                <small>No sales managers available</small>
                            )}
                            {managers.map(manager => (
                                <label key={manager._id} className="manager-checkbox-item">
                                    <input
                                        type="checkbox"
                                        checked={formData.managerIds.includes(manager._id)}
                                        onChange={() => toggleManager(manager._id)}
                                    />
                                    <span>{manager.fullName || manager.username}</span>
                                </label>
                            ))}
                        </div>
                        <small>Select one or more managers. Each can access this organization's data.</small>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={formData.active}
                                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                            />
                            <span>Active</span>
                        </label>
                        <small>Inactive organizations are hidden from the tree</small>
                    </div>

                    {/* Monthly Target Section */}
                    <div className="target-section">
                        <h3>Monthly Target</h3>
                        
                        {/* Month/Year Selection */}
                        <div className="period-selection">
                            <div className="form-group">
                                <label>Month:</label>
                                <select 
                                    value={selectedMonth} 
                                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                >
                                    {months.map((month, index) => (
                                        <option key={index + 1} value={index + 1}>
                                            {month}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Year:</label>
                                <select 
                                    value={selectedYear} 
                                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                >
                                    {Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() + i - 2).map(year => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Target Amount for {months[selectedMonth - 1]} {selectedYear}</label>
                            <input
                                type="number"
                                value={formData.target}
                                onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                                placeholder="Enter target amount"
                                min="0"
                                step="0.01"
                            />
                            <small>Set the sales target for the selected month</small>
                        </div>

                        {/* Target History */}
                        {targetHistory.length > 0 && (
                            <div className="target-history">
                                <h4>Recent Target History</h4>
                                <div className="history-list">
                                    {targetHistory.map((record, index) => (
                                        <div key={index} className="history-item">
                                            <span className="period">
                                                {months[record.month - 1]} {record.year}
                                            </span>
                                            <span className="amount">
                                                {record.target.toLocaleString()}
                                            </span>
                                            <span className="date">
                                                {new Date(record.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="modal-actions">
                        <button type="button" onClick={handleClose} className="btn-secondary" disabled={loading}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Updating...' : 'Update Organization'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditOrganizationModal;
